import React, { useState, useEffect } from "react";
import { Link } from "@quintype/components";
import "./desktop-header.m.css";

// Helper function to chunk array into groups of 9
const chunkArray = (array, chunkSize = 9) => {
  if (!array || !Array.isArray(array) || array.length === 0) {
    return [];
  }
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

export default function DesktopNavItem({ item, currentPath, allMenus, currentIndex }) {
  const children = item.children || [];
  const childrenChunks = children.length > 0 ? chunkArray(children, 9) : [];

  // State to track if we're on the client (hydrated)
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Helper to extract path from URL (handles both full URLs and relative paths)
  const extractPath = (url) => {
    if (!url) return '';
    try {
      // If it's a full URL, extract just the pathname
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        return urlObj.pathname;
      }
      // Already a relative path
      return url;
    } catch (e) {
      return url;
    }
  };

  // Check if current path matches this menu or its children
  const checkMatch = (menu, path) => {
    if (!path) return false;
    const cleanPath = path.split('?')[0].split('#')[0];

    // Direct match
    const menuPath = extractPath(menu.completeUrl);
    if (menuPath === cleanPath) return true;
    if (cleanPath.startsWith(menuPath + '/')) return true;

    // Check children (exclude placeholder and link items)
    if (menu.children && menu.children.length > 0) {
      for (const child of menu.children) {
        // Skip placeholder and link items
        const itemType = child['item-type'] || child.itemType;
        if (itemType === 'placeholder' || itemType === 'link') {
          continue;
        }

        const childUrl = child.completeUrl || child.url;
        const childPath = extractPath(childUrl);

        if (childPath === cleanPath) return true;
        if (cleanPath.startsWith(childPath + '/')) return true;

        // Check grandchildren (exclude placeholder and link items)
        if (child.children && child.children.length > 0) {
          for (const grandchild of child.children) {
            // Skip placeholder and link items
            const grandchildItemType = grandchild['item-type'] || grandchild.itemType;
            if (grandchildItemType === 'placeholder' || grandchildItemType === 'link') {
              continue;
            }

            const grandchildUrl = grandchild.completeUrl || grandchild.url;
            const grandchildPath = extractPath(grandchildUrl);
            if (grandchildPath === cleanPath) return true;
            if (cleanPath.startsWith(grandchildPath + '/')) return true;
          }
        }
      }
    }
    return false;
  };

  // Determine if this menu should be highlighted
  const active = (() => {
    if (!isClient) return false;
    if (!currentPath) return false;

    const cleanPath = currentPath.split('?')[0].split('#')[0];

    // FIRST: Check if a secondary menu was clicked - if so, NEVER highlight primary menus
    if (isClient) {
      try {
        const clicked = sessionStorage.getItem('clickedMenu');
        if (clicked) {
          const { url, type } = JSON.parse(clicked);
          // If clicked from secondary menu and current path matches, don't highlight any primary menu
          if (type === 'secondary' && url === cleanPath) {
            return false;
          }
        }
      } catch (e) {
        // Ignore storage errors
      }
    }

    // Check if this menu matches the current path
    const matches = checkMatch(item, currentPath);
    if (!matches) return false;

    // Check if user clicked from this specific primary menu
    if (isClient) {
      try {
        const clicked = sessionStorage.getItem('clickedMenu');
        if (clicked) {
          const { url, menuId, type, index } = JSON.parse(clicked);
          // If clicked from primary and URL matches, highlight if index matches (preferred) or ID matches
          if (type === 'primary' && url === cleanPath) {
            if (index !== undefined) {
              return currentIndex === index;
            }
            return item.id === menuId;
          }
        }
      } catch (e) {
        // Ignore storage errors
      }
    }

    // Default: only highlight if this is the first matching menu
    if (allMenus && currentIndex !== undefined) {
      for (let i = 0; i < currentIndex; i++) {
        if (checkMatch(allMenus[i], currentPath)) {
          return false;
        }
      }
    }

    return true;
  })();

  // Store which parent menu was clicked
  const storeMenuClick = (destinationUrl) => {
    try {
      // Extract path from URL (normalize full URLs to paths)
      const urlPath = extractPath(destinationUrl);
      const cleanUrl = urlPath.split('?')[0].split('#')[0];
      sessionStorage.setItem('clickedMenu', JSON.stringify({
        url: cleanUrl,
        menuId: item.id,
        type: 'primary',
        index: currentIndex
      }));
    } catch (e) {
      // Ignore storage errors
    }
  };

  return (
    <li styleName={active ? "primary-menu-item primary-menu-item-active" : "primary-menu-item"}>
      <div onMouseDown={() => storeMenuClick(item.completeUrl)}>
        <Link
          href={item.completeUrl}
          styleName={active ? "primary-menu-link-active" : "primary-menu-link"}
        >
          {item.title}
        </Link>
      </div>

      {children.length > 0 && (
        <div styleName="dropdown-menu">
          <div styleName="dropdown-menu-container">
            {childrenChunks.map((chunk, chunkIndex) => {
              const totalRows = childrenChunks[0].length;
              return (
                <ul key={chunkIndex} styleName="dropdown-menu-column">
                  {chunk.map((child, idx) => {
                    const isLastRow = idx + 1 === totalRows;
                    return (
                      <li key={idx} styleName={`dropdown-menu-item ${isLastRow ? 'last-row' : ''}`}>
                        <div styleName="dropdown-menu-link-wrapper" onMouseDown={() => storeMenuClick(child.completeUrl || child.url)}>
                          <Link
                            href={child.completeUrl || child.url}
                            styleName="dropdown-menu-link"
                          >
                            {child.title}
                          </Link>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              );
            })}
          </div>
        </div>
      )}
    </li>
  );
}
